import { type ReactNode } from 'react';
import { I } from '../lib/icons';
import { useVocab } from '../lib/use-vocab';
import { useTags } from '../lib/use-tags';
import { Pill } from './ui/Pill';
import { FilterCombobox } from './FilterCombobox';
import { ProductCategoryTree } from './ProductCategoryTree';
import type { StatsState } from '../../shared/asset-stats-types';
import type { AssetStatusFilter } from '../../shared/asset-constants';
// Typ/Status-Labels + AssetTypeFilter leben in lib/filter-options (Single-Source
// mit ActiveFilters). Re-Export von AssetTypeFilter unten hält bestehende
// Importe `from './FilterBar'` (LibraryPage) gültig.
import { STATUS_OPTIONS, TYPE_CHIPS, type AssetTypeFilter } from '../lib/filter-options';
import type { ChipFacet } from '../lib/active-filters';

export type { AssetTypeFilter };
export type FilterBarProps = Props;

// Inspector-Sektions-Label (gespiegelt vom Asset-Detail-Panel: uppercase,
// getrackt, gedämpft) — gibt dem Filter-Panel dieselbe ruhige Gliederung.
const LABEL_CLASS =
  'block mb-1.5 text-xs2 font-semibold uppercase tracking-[0.06em] text-brand-green-600';
const SELECT_CLASS =
  'w-full h-9 px-3 rounded-md text-xs font-semibold bg-white border border-ink-200 text-brand-green-800 hover:border-brand-green-700/40 cursor-pointer';

type Props = {
  type: AssetTypeFilter;
  onTypeChange: (t: AssetTypeFilter) => void;
  status: AssetStatusFilter;
  onStatusChange: (s: AssetStatusFilter) => void;
  /**
   * Taxonomie-Facetten-Filter — alle Multi-Select (`string[]`, leer = kein
   * Filter), je ein Toggle-Callback. `kategorie` ist seit 2026-06-11 ebenfalls
   * Multi-Select (Checkbox-Liste) + bleibt Cascade-Anker: gewählte Kategorien
   * → Vereinigung ihrer Produkte in der Produkt-Liste. Backend filtert
   * `p.kategorie_id IN (...)` (war schon csv-fähig).
   */
  kategorie: string[];
  onKategorieToggle: (id: string) => void;
  produkt: string[];
  onProduktToggle: (id: string) => void;
  verwendung: string[];
  onVerwendungToggle: (id: string) => void;
  /**
   * Tag-Filter (freie Tags, getrennt von der Produkt-Taxonomie). Multi-Select
   * als durchsuchbare Combobox; Werte sind lowercase Tag-Namen (URL-CSV `?tag=`).
   * Optionen lädt die FilterBar selbst via `useTags` (analog zu `useVocab` für
   * die Facetten); Counts kommen aus `stats.byTag`.
   */
  tag: string[];
  onTagToggle: (name: string) => void;
  /**
   * Ein-Klick „Taxonomie zurücksetzen" (IA-Follow-up 2026-06-10): clears nur die
   * 3 Browse-Facetten, behält Typ/Status/Suche/Tag. Der Link erscheint unten im
   * Taxonomie-Block nur, wenn ≥1 Facette aktiv ist.
   */
  onResetFacets: () => void;
  /**
   * Tri-State stats (Codex A1) — Loading/Error → counts hidden. OK → counts
   * pro type-chip + total auf "Alle".
   */
  stats: StatsState;
  /**
   * Nur EINE Sektion rendern (Redesign S2 Phase 2a): Inhalt eines Filter-Chip-
   * Popovers. Dieselben Controls + Counts wie im vollen Panel (Single-Source),
   * ohne „Taxonomie zurücksetzen" — das übernimmt im Chip-Modus das ×.
   * Fehlt `only`, rendert das volle Panel (Mobile-Filter-Drawer).
   */
  only?: ChipFacet;
};

/**
 * FilterBar — vertikales Filter-Panel. Seit Redesign S2 Phase 2a (2026-10-10)
 * kein rechtes Panel mehr: voll im Mobile-Filter-Drawer, sektionsweise (`only`)
 * als Inhalt der Filter-Chip-Popovers (`LibraryFilterChips`).
 * Historie: rechte Spalte der Library ab 2026-06-08.
 * Vorher horizontale Toolbar mit „Mehr"-Popover; auf User-Wunsch ins geordnete
 * Sektions-Layout des Asset-Detail-Inspectors überführt (Content links, Panel
 * rechts). Der Popover entfällt — alle Filter sind direkt sichtbar. Logik
 * (Counts, Cascade, Vokabular) unverändert; nur die Anordnung ist neu.
 */
export function FilterBar({
  type,
  onTypeChange,
  status,
  onStatusChange,
  kategorie,
  onKategorieToggle,
  produkt,
  onProduktToggle,
  verwendung,
  onVerwendungToggle,
  tag,
  onTagToggle,
  onResetFacets,
  stats,
  only,
}: Props) {
  const show = (section: ChipFacet): boolean => only === undefined || only === section;
  // Taxonomie-Pfad aktiv? (aus den Props — Single-Source). Steuert den
  // „Taxonomie zurücksetzen"-Link unten im Facetten-Block.
  const hasFacets =
    kategorie.length > 0 || produkt.length > 0 || verwendung.length > 0;
  // Counts pro type-chip — nur wenn stats geladen. Während Loading/Error
  // hidden (Labels bleiben, kein layout-shift wenn counts erscheinen).
  const statsOk = stats.status === 'ok' ? stats.stats : null;
  function chipCount(statsKey: 'img' | 'video' | 'pdf' | 'logo' | null): number | null {
    if (!statsOk) return null;
    return statsKey === null ? statsOk.total : statsOk.byType[statsKey];
  }

  // Phase 3: Facetten-Vokabular (shared useVocab). Kategorie = Single-Select,
  // Produkt/Verwendung = Multi-Select Toggle-Listen.
  const { state: vocabState } = useVocab();
  const vocabTree = vocabState.status === 'ok' ? vocabState.vocab : null;

  // Freie Tags (shared useTags, getrennt von der Produkt-Taxonomie). Optionen =
  // volle lowercase Tag-Liste; id = name = Filter-Wert (URL-CSV `?tag=`).
  const { state: tagsState } = useTags();
  const tagOptions =
    tagsState.status === 'ok' ? tagsState.tags.map((t) => ({ id: t, name: t })) : [];
  const tagEmptyHint =
    tagsState.status === 'loading'
      ? 'Tags werden geladen…'
      : tagsState.status === 'error'
        ? 'Tags konnten nicht geladen werden'
        : 'Noch keine Tags angelegt';

  // Count-Maps für die Toggle-Listen-Pills (nur bei stats.ok; Tri-State wie
  // type-chip-counts). Leere Maps wenn loading/error → keine Pills.
  const kategorieCounts = new Map(statsOk?.byKategorie.map((e) => [e.kategorieId, e.count]));
  const produktCounts = new Map(statsOk?.byProdukt.map((e) => [e.produktId, e.count]));
  const verwendungCounts = new Map(statsOk?.byVerwendung.map((e) => [e.verwendungId, e.count]));
  // Tag-Counts: keyed auf lowercase name (= tagOption.id). Mirror der Facetten-Maps.
  const tagCounts = new Map(statsOk?.byTag.map((e) => [e.name, e.count]));

  // Produkt & Kategorie = 2-stufige Browse-Hierarchie (Kategorie → Produkte).
  // Der inline-Baum bekommt den vollen Baum und routet die beiden Ebenen auf ihre
  // je eigenen Filter (kategorie / produkt). Verwendung bleibt eine flache
  // Toggle-Liste.
  const kategorieTree =
    vocabTree?.kategorien.map((k) => ({
      id: k.id,
      name: k.name,
      produkte: k.produkte.map((p) => ({ id: p.id, name: p.name })),
    })) ?? [];

  return (
    <div className="flex flex-col gap-5" role="group" aria-label="Filter">
      {/* Sort/Ansicht + Aktiver-Tag-Feld 2026-06-10 aus dem Panel raus: aktive
          Filter leben in der ActiveFilters-Chip-Leiste, Sort/Ansicht in der
          ResultsToolbar — beide über den Ergebnissen. FilterBar = rein
          Filter-Setzen, daher kein Darstellungs-Block + kein Trenner mehr. */}

      {/* Reihenfolge 2026-06-10 (IA Befund 4): die Produkt-Taxonomie
          (Produkt & Kategorie → Verwendung) führt — sie ist der primäre
          Browse-Einstieg. Typ + Status sind sekundäre Verfeinerungen und stehen
          darunter. */}

      {/* Produkt & Kategorie — sichtbarer inline-Baum (2026-06-15, IA-Fix). Die
          primäre Browse-Taxonomie steht direkt im Panel: Kategorien als wählbare
          Überschriften (→ kategorie-Filter), Produkte eingerückt darunter
          (→ produkt-Filter), erste N + „+N weitere". Löst die Dropdown-Combobox
          (#191) ab, die die Taxonomie hinter einem Klick versteckte und auf Mobile
          als Overlay über Verwendung/Tags ragte. Such-/NFC-/Chip-Logik unverändert. */}
      {show('taxonomy') && (
        <ProductCategoryTree
          kategorien={kategorieTree}
          selectedKategorie={kategorie}
          selectedProdukt={produkt}
          kategorieCounts={kategorieCounts}
          produktCounts={produktCounts}
          showCounts={statsOk !== null}
          onKategorieToggle={onKategorieToggle}
          onProduktToggle={onProduktToggle}
          emptyHint={vocabTree ? 'Keine Kategorien' : 'Vokabular wird geladen…'}
        />
      )}
      {show('verwendung') && (
        <FacetToggleList
          label="Verwendung"
          options={vocabTree?.verwendungen.map((v) => ({ id: v.id, name: v.name })) ?? []}
          selected={verwendung}
          counts={verwendungCounts}
          showCounts={statsOk !== null}
          onToggle={onVerwendungToggle}
        />
      )}

      {/* Ein-Klick „Taxonomie zurücksetzen" (IA-Follow-up): nur sichtbar wenn
          ≥1 Facette aktiv. Clears nur den Browse-Pfad (Kategorie/Produkt/
          Verwendung) — Typ/Status/Suche/Tag bleiben. Abgegrenzt von „Alle
          zurücksetzen" in der Chip-Leiste über den Ergebnissen. */}
      {hasFacets && only === undefined && (
        <button
          type="button"
          onClick={onResetFacets}
          className="-mt-1 self-start text-xs2 font-semibold text-brand-green-600 hover:text-brand-green-800 hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-1"
        >
          Taxonomie zurücksetzen
        </button>
      )}

      {/* Tags — freie Tags (getrennt von der Produkt-Taxonomie). Durchsuchbare
          Combobox: User kennen die Tags nicht auswendig, daher Liste zum Browsen
          + Tippen zum Filtern (analog Produkt). Vom „Taxonomie zurücksetzen"-Link
          bewusst unberührt — Tags sind ein eigener Filter. */}
      {show('tag') && (
        <FilterCombobox
          label="Tags"
          options={tagOptions}
          selected={tag}
          counts={tagCounts}
          showCounts={statsOk !== null}
          onToggle={onTagToggle}
          placeholder="Tag suchen oder wählen…"
          emptyHint={tagEmptyHint}
        />
      )}

      {/* Typ — sekundäre Verfeinerung (Single-Select Pills, "Alle" = kein Filter). */}
      {show('type') && (
        <Field label="Typ">
          <div className="flex flex-wrap gap-1.5">
            {TYPE_CHIPS.map((chip) => {
              const active = type === chip.value;
              const count = chipCount(chip.statsKey);
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => onTypeChange(chip.value)}
                  aria-pressed={active}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    active
                      ? 'bg-brand-green-700 text-brand-cream border-brand-green-700'
                      : 'bg-white text-brand-green-800 border-ink-200 hover:border-brand-green-700/40'
                  }`}
                >
                  {chip.label}
                  {count !== null && (
                    <span className="ml-1.5 font-mono opacity-75">{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {/* Status — sekundäre Verfeinerung */}
      {show('status') && (
        <Field label="Status">
          <select
            value={status}
            onChange={(e) => onStatusChange(e.target.value as AssetStatusFilter)}
            aria-label="Status-Filter"
            className={SELECT_CLASS}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      )}
      {/* „Facetten zurücksetzen" 2026-06-10 entfernt: per-Chip-× + „Alle
          zurücksetzen" in der ActiveFilters-Leiste ersetzen es. */}
    </div>
  );
}

// Sektions-Wrapper: uppercase-Label + Control darunter (Inspector-Stil).
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className={LABEL_CLASS}>{label}</span>
      {children}
    </div>
  );
}

// Multi-Select-Facette als Toggle-Liste mit Count-Pills (User-Decision, Sidebar-
// Stil). Pro Zeile: Checkbox-Indikator (`role="checkbox"`) + Name + Count-Pill
// (nur wenn `showCounts` UND count>0 — Regel wie Sidebar, kein „0"/„?"-Noise).
// Scrollbar bei vielen Optionen (`max-h` + overflow). Keyboard: <button> ist
// nativ fokus-/Enter-/Space-bedienbar.
type FacetToggleListProps = {
  label: string;
  options: { id: string; name: string }[];
  selected: string[];
  counts: Map<string, number>;
  showCounts: boolean;
  onToggle: (id: string) => void;
};

// Eine Checkbox-Zeile (genutzt von FacetToggleList — Kategorie/Verwendung):
// Indikator + Name + optionale Count-Pill. <button role="checkbox">.
function FacetCheckboxRow({
  opt,
  isSelected,
  count,
  showCounts,
  onToggle,
}: {
  opt: { id: string; name: string };
  isSelected: boolean;
  count: number;
  showCounts: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isSelected}
      onClick={() => onToggle(opt.id)}
      className="flex items-center gap-2 px-1.5 py-1 rounded text-xs text-left hover:bg-ink-100 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700"
    >
      <span
        aria-hidden="true"
        className={`flex items-center justify-center shrink-0 w-4 h-4 rounded border transition ${
          isSelected
            ? 'bg-brand-green-700 border-brand-green-700 text-brand-cream'
            : 'bg-white border-ink-300'
        }`}
      >
        {isSelected && <I.check size={10} />}
      </span>
      <span className={`flex-1 truncate ${isSelected ? 'font-semibold text-brand-green-700' : 'text-brand-green-800'}`}>
        {opt.name}
      </span>
      {showCounts && count > 0 && (
        <Pill tone="neutral" aria-label={`${count} Asset(s)`}>
          {count}
        </Pill>
      )}
    </button>
  );
}

// Multi-Select als immer-sichtbare Checkbox-Liste (Verwendung — kuratiertes
// Vokabular, „alle Optionen auf einen Blick"). Kein innerer Scroll: ein max-h
// schnitt sonst die letzte Zeile mittig an (sah aus wie ein Darstellungsfehler);
// die Liste wächst frei, das Panel als Ganzes scrollt mit der Seite.
function FacetToggleList({
  label,
  options,
  selected,
  counts,
  showCounts,
  onToggle,
}: FacetToggleListProps) {
  return (
    <div role="group" aria-label={`${label}-Filter`}>
      <span className={LABEL_CLASS}>{label}</span>
      {options.length === 0 ? (
        <span className="text-xs2 text-brand-green-600 px-1 py-0.5">Keine Optionen</span>
      ) : (
        <div className="flex flex-col -mx-1 px-1">
          {options.map((opt) => (
            <FacetCheckboxRow
              key={opt.id}
              opt={opt}
              isSelected={selected.includes(opt.id)}
              count={counts.get(opt.id) ?? 0}
              showCounts={showCounts}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

